import { BffApiClient } from '@/common/services/api/bffClient';
import {
  ChatMessageReceivedEvent,
  ChatThreadCreatedEvent,
  ChatThreadDeletedEvent,
  ChatThreadPropertiesUpdatedEvent,
  TypingIndicatorReceivedEvent,
} from '@azure/communication-chat';
import { chatEndpoints } from '../endpoints/chatEndpoints';
import { AZURE_CHAT_CONFIG } from '../config/chatConfig';
import { AzureChatManager } from './AzureChatManager';
import { ChatApiClient } from './ChatApiClient';
import { StudentsSearchService } from './StudentsSearchService';
import type {
  AcsToken,
  Conversation,
  Message,
  CreateThreadRequest,
  SendMessageDto,
  SendAttachmentDto,
  UpdateTopicDto,
  ConversationParticipant,
  Student,
} from '../types';

// Import lazy du store pour éviter les dépendances circulaires
import { useChatStore } from '../stores/chatStore';
function getChatStore() {
  return useChatStore();
}

/**
 * Chat Service — couche API BFF + orchestration temps réel ACS.
 *
 * - Le token ACS est récupéré via BffApiClient puis stocké dans ChatApiClient.
 * - Les appels REST métier passent par ChatApiClient (qui injecte le acsToken via header).
 * - Le temps réel (messages, typing, read receipts) est délégué à AzureChatManager.
 * - Les événements temps réel mettent à jour le chatStore directement (plus de window.CustomEvent).
 * - StudentsSearchService reste sur WardApiClient (routes hors BFF).
 */
export class ChatService {
  private static azureChatManager = new AzureChatManager();

  private static get api() {
    return ChatApiClient.getInstance();
  }

  private static get bffClient() {
    return BffApiClient.getInstance();
  }

  // ────────────────────────────────────────────
  // Initialisation & événements temps réel
  // ────────────────────────────────────────────

  static async initializeAzureChat(email: string, notificationToken?: string): Promise<AcsToken> {
    const acsToken = await this.getAcsToken(email, AZURE_CHAT_CONFIG.PLATFORM, notificationToken);
    this.api.setAcsToken(acsToken);
    this.api.setRefreshTokenFn(() =>
      this.getAcsToken(email, AZURE_CHAT_CONFIG.PLATFORM, notificationToken)
    );
    await this.azureChatManager.initialize(AZURE_CHAT_CONFIG.ACS_ENDPOINT, acsToken.token);
    this.setupChatEventListeners();
    return acsToken;
  }

  private static setupChatEventListeners(): void {
    this.azureChatManager.addEventListener('messageReceived', this.handleNewMessage.bind(this));
    this.azureChatManager.addEventListener('threadCreated', this.handleNewThread.bind(this));
    this.azureChatManager.addEventListener('threadUpdated', this.handleThreadUpdate.bind(this));
    this.azureChatManager.addEventListener('threadDeleted', this.handleThreadDeleted.bind(this));
    this.azureChatManager.addEventListener(
      'typingIndicator',
      this.handleTypingIndicator.bind(this)
    );
  }

  private static handleNewMessage(event: ChatMessageReceivedEvent): void {
    const attachments = (event.attachments ?? []).map((att: any) => ({
      id: att.id || `attachment-${Date.now()}`,
      name: att.name || att.fileName || 'Unknown file',
      url: att.url || att.previewUrl || '',
      contentType: att.contentType || 'application/octet-stream',
    }));

    getChatStore().onNewMessage({
      threadId: event.threadId,
      messageId: event.id,
      content: event.message,
      senderName: event.senderDisplayName,
      senderIdentity: (event.sender as any).communicationUserId || '',
      createdOn: event.createdOn.toISOString(),
      attachments,
    });
  }

  private static handleNewThread(event: ChatThreadCreatedEvent): void {
    getChatStore().onNewThread({
      threadId: event.threadId,
      topic: event.properties.topic,
    });
  }

  private static handleThreadUpdate(event: ChatThreadPropertiesUpdatedEvent): void {
    getChatStore().onThreadUpdated({
      threadId: event.threadId,
      topic: event.properties.topic,
    });
  }

  private static handleThreadDeleted(event: ChatThreadDeletedEvent): void {
    getChatStore().onConversationDeleted({ threadId: event.threadId });
  }

  private static handleTypingIndicator(event: TypingIndicatorReceivedEvent): void {
    getChatStore().onTypingIndicator({
      threadId: event.threadId,
      senderName: event.senderDisplayName,
    });
  }

  // ────────────────────────────────────────────
  // Délégation temps réel → AzureChatManager
  // ────────────────────────────────────────────

  static async sendTypingNotification(threadId: string): Promise<void> {
    return this.azureChatManager.sendTypingNotification(threadId);
  }

  static async markMessagesAsRead(threadId: string, messages: Message[]): Promise<void> {
    if (messages.length === 0) return;
    const lastMessage = messages[messages.length - 1];
    return this.azureChatManager.markMessagesAsRead(threadId, lastMessage.id);
  }

  static async cleanup(): Promise<void> {
    this.api.clearAcsToken();
    return this.azureChatManager.cleanup();
  }

  // ────────────────────────────────────────────
  // API BFF — Token (avant que le acsToken ne soit dispo)
  // ────────────────────────────────────────────

  static async getAcsToken(
    email: string,
    platform = 'fcm',
    notificationToken?: string
  ): Promise<AcsToken> {
    const response = await this.bffClient.get<AcsToken>(
      chatEndpoints.getToken(email, platform, notificationToken)
    );
    return response.data;
  }

  // ────────────────────────────────────────────
  // API BFF — Conversations
  // ────────────────────────────────────────────

  static async getConversations(): Promise<Conversation[]> {
    const response = await this.api.get<Conversation[]>(chatEndpoints.getConversations);
    return response.data;
  }

  static async createConversation(request: CreateThreadRequest): Promise<string> {
    const response = await this.api.post<string>(chatEndpoints.createConversation, request);
    return response.data;
  }

  static async deleteConversation(threadId: string): Promise<void> {
    await this.api.delete(chatEndpoints.deleteConversation(threadId));
    getChatStore().onConversationDeleted({ threadId });
  }

  // ────────────────────────────────────────────
  // API BFF — Participants
  // ────────────────────────────────────────────

  static async addUsersToThread(
    threadId: string,
    participants: ConversationParticipant[]
  ): Promise<string> {
    const response = await this.api.post<string>(
      chatEndpoints.addParticipants(threadId),
      participants
    );
    return response.data;
  }

  static async removeUserFromThread(
    threadId: string,
    participant: ConversationParticipant
  ): Promise<void> {
    await this.api.delete(chatEndpoints.removeParticipant(threadId, participant.mail));
  }

  // ────────────────────────────────────────────
  // API BFF — Topic
  // ────────────────────────────────────────────

  static async updateThreadTopic(threadId: string, topic: UpdateTopicDto): Promise<string> {
    const response = await this.api.put<string>(chatEndpoints.updateTopic(threadId), topic);
    return response.data;
  }

  // ────────────────────────────────────────────
  // API BFF — Messages
  // ────────────────────────────────────────────

  static async getMessagesByThread(threadId: string, limit?: number): Promise<Message[]> {
    const response = await this.api.get<Message[]>(chatEndpoints.getMessages(threadId, limit));
    return response.data;
  }

  static async sendMessage(threadId: string, data: SendMessageDto): Promise<string> {
    const response = await this.api.post<string>(chatEndpoints.sendMessage(threadId), data);
    return response.data;
  }

  static async sendAttachment(
    threadId: string,
    data: SendAttachmentDto,
    file: File
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('body', JSON.stringify(data));
    const response = await this.api.post<string>(chatEndpoints.sendAttachment(threadId), formData);
    return response.data;
  }

  // ────────────────────────────────────────────
  // Utilitaires
  // ────────────────────────────────────────────

  static async searchStudents(query: string): Promise<Student[]> {
    return StudentsSearchService.searchStudents(query);
  }

  static getUserIdentityFromToken(token: string): string {
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return `8:${payload.skypeid}`;
  }

  static getUserNameFromToken(token: string): string {
    try {
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      return payload.name || 'Utilisateur ACS';
    } catch {
      return 'Utilisateur ACS';
    }
  }

  static isAttachmentUrlNotExpired(url: string): boolean {
    const match = url.match(/se=([^&]+)/);
    return match ? new Date(decodeURIComponent(match[1])) > new Date() : false;
  }
}
